import React, { useState, useEffect } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import FileUpload from '../../components/UI/FileUpload';
import NotFound from '../../components/UI/NotFound';
import { imageType, MAX_FILE_SIZE_IMAGE, invalidImageMessage } from '../../components/imageType';
import { imageSizeExceededMessage } from '../../components/imageType';
import { FaImage, FaImages, FaUpload, FaRegEye } from 'react-icons/fa';
import { HiOutlineRefresh } from 'react-icons/hi';

const UIIntroScreen = () => {
    const [loader, setLoader] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState(null);
    const [screens, setScreens] = useState([]);
    const [editingScreens, setEditingScreens] = useState({});
    const [fileProcessing, setFileProcessing] = useState({});
    const [fileStatus, setFileStatus] = useState({});
    const navigate = useNavigate();


    useEffect(() => {
        fetchScreens();
    }, []);

    const fetchScreens = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getIntroScreen();
            console.log(resp);
            if (resp?.status) {
                setScreens(resp?.data);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Fetch error:", error.response?.data || error.message);
            // navigate('/Error');
        } finally {
            setLoader(false);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const fieldName = event.target.name || 'image';

        setError(null);
        setSelectedFile(null);
        setFileProcessing({ ...fileProcessing, [fieldName]: true });
        setFileStatus({ ...fileStatus, [fieldName]: null });

        if (!file) {
            setError('No file selected.');
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        if (!imageType.includes(file.type)) {
            setError(invalidImageMessage);
            toast.error(invalidImageMessage);
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        if (file.size > MAX_FILE_SIZE_IMAGE) {
            setError(imageSizeExceededMessage);
            toast.error(imageSizeExceededMessage);
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        setSelectedFile(file);
        setFileProcessing({ ...fileProcessing, [fieldName]: false });
        setFileStatus({ ...fileStatus, [fieldName]: 'success' });
    };

    const resetForm = () => {
        setSelectedFile(null);
        setError(null);
        setFileStatus({});
    };

    const handleSubmit = async (e, screenId = null) => {
        e.preventDefault();

        if (!selectedFile && !screenId) {
            setError('Please select a valid file before submitting.');
            return;
        }

        setLoader(true);
        try {
            const formData = new FormData();
            if (selectedFile) {
                formData.append('image', selectedFile);
            }

            let resp;
            if (screenId) {
                resp = await Calling.updateIntroScreen(screenId, formData);
            } else {
                resp = await Calling.addIntroScreen(formData);
            }

            if (resp?.status) {
                toast.success(resp?.message);
                resetForm();
                setEditingScreens({});
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Submission error:", error.response?.data || error.message);
            toast.error("Something went wrong.");
        } finally {
            setLoader(false);
            await fetchScreens();
        }
    };

    const handleEdit = (screen) => {
        setEditingScreens((prev) => ({ ...prev, [screen._id]: true }));
        setFileStatus({});
    };

    const handleDelete = async (id) => {
        const content = "You are about to delete this item. To confirm, please solve this math problem.";
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');

        setLoader(true);
        try {
            const resp = await Calling.deleteIntroScreen(id);
            if (resp?.status) {
                toast.success(resp?.message);
                setScreens(screens.filter((screen) => screen._id !== id));
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Delete error:", error.response?.data || error.message);
            navigate('/Error');
        } finally {
            setLoader(false);
        }
    };

    const handleUpdateState = async (id) => {
        const content = "You are about to Update this item status. To confirm, please solve this math problem.";
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');
        setLoader(true);
        try {
            const resp = await Calling.updateIntroScreenStatus(id);
            if (resp?.status) {
                toast.success(resp?.message);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Delete error:", error.response?.data || error.message);
            navigate('/Error');
        } finally {
            setLoader(false);
            fetchScreens();
        }
    };


    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className="bg-gray-50 w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
                    <div className="mx-auto">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">UI Management</h1>
                                <p className="mt-1 text-sm text-gray-500">Manage your application intro screens</p>
                            </div>
                            <button
                                onClick={fetchScreens}
                                className="flex items-center gap-2 px-4 py-2 bg-main-blue hover:bg-main-blue/80 rounded-lg text-white transition-colors duration-200 shadow-sm"
                            >
                                <HiOutlineRefresh className="text-lg" />
                                <span>Refresh</span>
                            </button>
                        </div>


                        {
                            screens?.length < 5 && (
                                <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100 mb-6">
                                    <div className="p-5 border-b border-gray-100">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                                <FaUpload className="text-main-blue text-xl" />
                                            </div>
                                            <div>
                                                <h2 className="text-lg font-semibold text-gray-800">Add Intro Screen</h2>
                                                <p className="text-xs text-gray-500 mt-0.5">Upload new intro screen images</p>
                                            </div>
                                        </div>
                                    </div >

                                    <div className="p-5 bg-gray-50/50">
                                        <form onSubmit={(e) => handleSubmit(e)} className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div className="md:col-span-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <FaImage className="text-main-blue text-sm" />
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            Screen Image
                                                        </label>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mb-2">Upload intro screen image</p>
                                                </div>
                                                <div className="md:col-span-2">
                                                    <div className="relative">
                                                        <FileUpload
                                                            name="image"
                                                            id="image"
                                                            onChange={handleFileChange}
                                                            accept={imageType.join(',')}
                                                            required={true}
                                                            multiple={false}
                                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                                                        />
                                                        {fileProcessing['image'] && (
                                                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                                <div className="loader"></div>
                                                            </div>
                                                        )}
                                                        {fileStatus['image'] === 'error' && (
                                                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xl text-red-500">✘</div>
                                                        )}
                                                        {fileStatus['image'] === 'success' && (
                                                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xl text-green-500">✓</div>
                                                        )}
                                                    </div>
                                                    <p className={`text-xs mt-2 ${error ? 'text-red-500' : 'text-gray-500'}`}>{error ? error : ''}</p>
                                                    <p className='text-xs mt-1 text-gray-400'>Allow png, jpg, jpeg / Max size: {MAX_FILE_SIZE_IMAGE / (1024 * 1024)} MB</p>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-end gap-3 pt-2">
                                                <button
                                                    type="button"
                                                    onClick={resetForm}
                                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                                                >
                                                    Reset
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 transition-colors duration-200 flex items-center gap-2"
                                                >
                                                    <FaUpload className="text-sm" />
                                                    Upload
                                                </button>
                                            </div>
                                        </form>
                                    </div>

                                </div>
                            )
                        }

                        {/* Uploaded Screens Card */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100">
                            <div className="p-5 border-b border-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-purple-100 rounded-lg">
                                        <FaImages className="text-main-blue text-xl" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">Uploaded Screens</h2>
                                        <p className="text-xs text-gray-500 mt-0.5">Manage your existing intro screens</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                {screens.length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                        {screens.map((screen, index) => (
                                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300" key={screen._id}>
                                                <div className="p-3 border-b border-gray-100 flex justify-between items-center">
                                                    <div className="font-medium text-gray-800 flex items-center gap-2">
                                                        <FaRegEye className="text-purple-500" />
                                                        Screen {index + 1}
                                                    </div>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={screen.status}
                                                            onChange={() => handleUpdateState(screen?._id)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="relative w-11 h-6 bg-gray-200 rounded-full peer 
                                                            peer-checked:after:translate-x-full peer-checked:after:border-white 
                                                            after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                                            after:bg-white after:border-gray-300 after:border after:rounded-full 
                                                            after:h-5 after:w-5 after:transition-all dark:border-gray-600 
                                                            peer-checked:bg-main-blue"></div>
                                                    </label>
                                                </div>
                                                <div className="w-full overflow-hidden">
                                                    <img
                                                        src={screen?.ui_image}
                                                        alt="intro screen"
                                                        className="w-full h-48 object-cover transition-transform duration-300 hover:scale-105"
                                                    />
                                                </div>
                                                <div className="p-3">
                                                    {editingScreens[screen._id] ? (
                                                        <form onSubmit={(e) => handleSubmit(e, screen._id)} className="w-full">
                                                            <div className="mb-4">
                                                                <div className="relative">
                                                                    <FileUpload
                                                                        name="image"
                                                                        id={`image-${screen._id}`}
                                                                        onChange={handleFileChange}
                                                                        accept={imageType.join(',')}
                                                                        required
                                                                        multiple={false}
                                                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                                                                    />
                                                                    {fileProcessing['image'] && (
                                                                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                                            <div className="loader"></div>
                                                                        </div>
                                                                    )}
                                                                    {fileStatus['image'] === 'error' && (
                                                                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xl text-red-500">✘</div>
                                                                    )}
                                                                    {fileStatus['image'] === 'success' && (
                                                                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xl text-green-500">✓</div>
                                                                    )}
                                                                </div>
                                                                <p className={`text-xs mt-2 ${error ? 'text-red-500' : 'text-gray-500'}`}>{error ? error : ''}</p>
                                                                <p className='text-xs mt-1 text-gray-400'>Allow png, jpg, jpeg / Max size: {MAX_FILE_SIZE_IMAGE / (1024 * 1024)} MB</p>
                                                            </div>
                                                            <div className="flex justify-end">
                                                                <button
                                                                    type="submit"
                                                                    className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 transition-colors duration-200 flex items-center gap-2"
                                                                >
                                                                    <FaUpload className="text-sm" />
                                                                    Upload
                                                                </button>
                                                            </div>
                                                        </form>
                                                    ) : (
                                                        <div className="flex flex-col sm:flex-row justify-center items-center gap-2 w-full">
                                                            <button
                                                                className="text-main-blue border border-main-blue px-4 py-2 rounded-lg hover:bg-main-thHover transition-all focus:outline-none w-full sm:w-auto text-center font-medium flex items-center justify-center gap-1"
                                                                onClick={() => handleEdit(screen)}
                                                            >
                                                                <FaUpload className="text-sm" />
                                                                Replace
                                                            </button>
                                                            <button
                                                                className="text-red-600 border border-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-all focus:outline-none w-full sm:w-auto text-center font-medium flex items-center justify-center gap-1"
                                                                onClick={() => handleDelete(screen._id)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <NotFound />
                                )}
                            </div>
                        </div>
                    </div >
                </div >
            )}
        </>
    );
};

export default UIIntroScreen;
